//  ----------      START   IMPORT  BLOCK       ----------
import { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import useDebouncedFetch from '../../../../API/Hooks/useDebouncedFetch';
import './QuillBolts'
//  ----------      END   IMPORT  BLOCK       ----------


const DiagnosisTextBox = ({placeholder, listBox, noItemMessage, errorMessage, promise, onTextChange, onTagsChange }) => {
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  
  const [extractedQuotes, setExtractedQuotes] = useState([]);
  const [activeQuote, setActiveQuote] = useState('');
  const quoteStartIndexRef = useRef(null);
  const quoteEndIndexRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const activeIndexRef = useRef(activeIndex);

  const confirmed_cie_tags = useRef([]);


  const [query, setQuery] = useState("");
  const [data2, setData2, error2] = useDebouncedFetch(activeQuote, promise);
  const dataRef = useRef(data2);  

  const appendCIEtag = (newObject) => {
    const currentList = confirmed_cie_tags.current;

    //  Check if the id_cie is in the array.
    const isIdPresent = currentList.some((item) => item._cie === newObject.id_cie);

    if (!isIdPresent) {
      // If id_cie is not present, append it.
      confirmed_cie_tags.current = [...currentList, newObject];
    }
  };

  const handleEnterKey = (range, context) => {
    if (activeIndexRef.current == null || dataRef.current == null) {
      //  Important! This allows the default behavior to proceed (break line).
      return true; 
    };
    //  Use the stored start and end positions of activeQuote to replace the text inside the quotes.
    if (quoteStartIndexRef.current !== null && quoteEndIndexRef.current !== null) {
      const selectedObject = dataRef.current[activeIndexRef.current]
      //  Append the tag used for font color.
      appendCIEtag(selectedObject)
      const currentText = quillInstance.current.getText();
      const newText = currentText.slice(0, quoteStartIndexRef.current) + selectedObject.name + currentText.slice(quoteEndIndexRef.current);
      quillInstance.current.setText(newText);
      quillInstance.current.setSelection(quoteStartIndexRef.current + selectedObject.name.length + 1);
      //  Clear related variables.
      setActiveQuote('');
      setQuery('')
      setActiveIndex(null);
      //  Important! This prevents the default behavior to proceed (break line).
      return false;
    }
    //  Important! This allows the default behavior to proceed (break line).
    return true;
  };

  const bindings = {
    ENTER: {
      key: "Enter",
      handler: handleEnterKey
    }
  }

  useEffect(() => {
    dataRef.current = data2;
    console.log('data2', data2)
  }, [data2]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    quillInstance.current = new Quill(quillRef.current, {
      theme: 'snow',
      modules: {
        toolbar: false,
        keyboard: {
          bindings: bindings
        }
      },
      placeholder: placeholder
    });

    quillInstance.current.on('text-change', handleTextChange);
    //  Double quotes.
    quillInstance.current.keyboard.addBinding({ key: 50, shiftKey: true }, handleQuoteKey);
    //  Arrow down.
    quillInstance.current.keyboard.addBinding({ key: 40 }, handleArrowDown);
    //  Arrow up.
    quillInstance.current.keyboard.addBinding({ key: 38 }, handleArrowUp);
    //  Enter (disabled due to bug)
    //  quillInstance.current.keyboard.addBinding({ key: 13 }, handleEnterKey);
  }, []);

  const handleTextChange = () => {
    const content = quillInstance.current.getText();

    // Send the text content to the parent via onTextChange callback
    if (onTextChange) {
      onTextChange(content);
    }
    if (onTagsChange) {
      onTagsChange(confirmed_cie_tags.current);
    }

    //  Extract text inside double quotes.
    extractQuotes(content);
    //  Query active quoted text.
    setQuery(activeQuote);
  };

  const handleQuoteKey = (range) => {
    const { index } = range;
    quillInstance.current.insertText(index, `""`);
    quillInstance.current.setSelection(index + 1);
  };
  


  const extractQuotes = (text) => {
    //  Filter quoted text.
    const regex = /"([^"]*)"/g;
    const matches = [];
    const positions = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]);
      positions.push({ start: match.index + 1, end: regex.lastIndex - 1 });
    }
    //  Return if no quotes are present.
    if (positions.length === 0){
      return;
    }
    setExtractedQuotes(positions);
    ///--------------------
    applyQuoteFormat(positions);
    ///--------------------
    updateActiveQuote(text, quillInstance.current.getSelection()?.index || 0);
  };

  ///--------------------

  const chapterCodeMap = {
  '1': 'chapter01Color', '2': 'chapter02Color', '3': 'chapter03Color', '4': 'chapter04Color',
  '5': 'chapter05Color', '6': 'chapter06Color', '7': 'chapter07Color', '8': 'chapter08Color',
  '9': 'chapter09Color', 'A': 'chapter10Color', 'B': 'chapter11Color', 'C': 'chapter12Color',
  'D': 'chapter13Color', 'E': 'chapter14Color', 'F': 'chapter15Color', 'G': 'chapter16Color',
  'H': 'chapter17Color', 'I': 'chapter18Color', 'J': 'chapter19Color', 'K': 'chapter20Color',
  'L': 'chapter21Color', 'M': 'chapter22Color', 'N': 'chapter23Color', 'P': 'chapter24Color',
  'Q': 'chapter25Color', 'R': 'chapter26Color', 'S': 'chapter27Color',
};

  const findMatchingObject = (searchString) => {
    const currentList = confirmed_cie_tags.current;
    
    // 1. Find the confirmed tag object that matches the text inside the quotes (searchString)
    const matchedItem = currentList.find(item => item.name === searchString);

    // 2. If no match is found, or if id_cie is missing, return the default color
    if (!matchedItem || !matchedItem.id_cie) {
      // console.log('No matching tag found for coloring:', searchString);
      return { chapter00Color: true };
    }

    // 3. Use the first character of id_cie to look up the color name in the map
    const chapterPrefix = matchedItem.id_cie.slice(0, 1).toUpperCase();
    const blotName = chapterCodeMap[chapterPrefix] || 'chapter00Color';
    
    // console.log('Color found for ID:', matchedItem.id_cie, '->', blotName);
    return { [blotName]: true };
  };
  
  
  const applyQuoteFormat = (quotePositions) => {
    //  Disabling text change handling (prevents entering an infinite loop).
    quillInstance.current.off('text-change', handleTextChange);
  
    const delta = quillInstance.current.getContents();
    const newOps = [];
    let textIndex = 0;
    //  Save current cursor position.
    const currentRange = quillInstance.current.getSelection();

  
    delta.ops.forEach((op) => {
      if (typeof op.insert === 'string') {
        let str = op.insert;
        let lastIndex = 0;
  
        quotePositions.forEach(({ start, end }) => {
          if (start >= textIndex && start < textIndex + str.length) {
            const localStart = start - textIndex;
            const localEnd = end - textIndex;
  
            if (localStart > lastIndex) {
              newOps.push({ insert: str.slice(lastIndex, localStart) });
            }
            //  Text to be matched with CIE entries.
            // Cleaned up code in applyQuoteFormat:
            const quoteText = str.slice(localStart, localEnd);
            // Decide color by chapter using the defined logic.
            const attributes = findMatchingObject(quoteText)
                        
            newOps.push({ insert: quoteText, attributes: attributes});
            ///--------------------

            //newOps.push({ insert: str.slice(localStart, localEnd), attributes: { quote: true } });
            lastIndex = localEnd;
          }
        });
  
        if (lastIndex < str.length) {
          newOps.push({ insert: str.slice(lastIndex) });
        }
      } else {
        newOps.push(op);
      }
      textIndex += op.insert.length || 0;
    });
    //  Insert colored text (as side effect changes cursor position to 0).
    quillInstance.current.setContents({ ops: newOps });
    //  Restore the cursor position to it's original.
    const index = Math.min(currentRange.index, quillInstance.current.getLength() - 1);
    quillInstance.current.setSelection(index);
    //  Restore text change handling.
    quillInstance.current.on('text-change', handleTextChange);
  };  
  ///--------------------

  const updateActiveQuote = (text, cursorPosition) => {
    //  Determines which quote the user is currently at saves initial/ending positions.
    const regex = /"([^"]*)"/g;
    let match;
    let currentActiveQuote = '';
    //  Determines if there are double quotes to begin with.
    while ((match = regex.exec(text)) !== null) {
      //  Store each start/end positions of each match.
      const start = match.index;
      const end = regex.lastIndex;
      //  Review if cursor position is within any of the ranges.
      if (cursorPosition > start && cursorPosition < end) {
        //  If within a range mark quote as active.
        currentActiveQuote = match[1];
        //  +1 to exclude the opening quote
        quoteStartIndexRef.current = start + 1;
        //  -1 to exclude the closing quote
        quoteEndIndexRef.current = end - 1;
        break;
      }
    }
    setActiveQuote(currentActiveQuote);
  };

  const handleArrowDown = (range, context) => {
    const event = context.event;
    if (!dataRef.current || dataRef.current.length === 0) return true;
    //  Prevents movements inside the text, movements take place inside the suggestions list.
    event.preventDefault();
    //  Changes active index (representing selection).
    setActiveIndex((prevIndex) => (prevIndex === null || prevIndex === dataRef.current.length - 1 ? 0 : prevIndex + 1));
    //  Expected bool as return.
    return false;
  };

  const handleArrowUp = (range, context) => {
    const event = context.event;
    if (!dataRef.current || dataRef.current.length === 0) return true;
    //  Prevents movements inside the text, movements take place inside the suggestions list.
    event.preventDefault();
    //  Changes active index (representing selection).
    setActiveIndex((prevIndex) => (prevIndex === 0 ? dataRef.current.length - 1 : prevIndex - 1));
    //  Expected bool as return.
    return false;
  };

  useEffect(() => {
    if (quillInstance.current) {
      quillInstance.current.on('selection-change', (range) => {
        if (range) {
          const content = quillInstance.current.getText();
          updateActiveQuote(content, range.index);
        }
      });
    }
  }, []);

  return (
    <div>
      
      <div ref={quillRef} style={{ height: '300px' }}></div>
      {query && data2 && data2.length === 0 && noItemMessage()}
      {error2 && errorMessage()}
      {(() => { 
        if (activeQuote) {
          return listBox(data2 || [], activeIndex)
        }
      })()}


    </div>
  );
};

export default DiagnosisTextBox;
